import React from "react";
import { createBrowserRouter, Navigate, useParams } from "react-router-dom";

import { ProtectedRoute } from "@/auth/ProtectedRoute";
import { Layout } from "@/components/Layout";
import { DashboardPage } from "@/pages/DashboardPage";
import { LoginPage } from "@/pages/LoginPage";
import { AdminUsersPage } from "@/pages/admin/AdminUsersPage";
import { OneCBasesAdminPage } from "@/pages/admin/OneCBasesAdminPage";
import { OzonFinishPage } from "@/pages/ozon/FinishPage";
import { OzonHome } from "@/pages/ozon/OzonHome";
import { OzonScanPage } from "@/pages/ozon/OzonScanPage";
import { ActivityPage } from "@/pages/ActivityPage";
import { FbsHomePage } from "@/pages/fbs/FbsHomePage";
import { FbsFinishPage } from "@/pages/fbs/FinishPage";
import { FbsSuppliesPage } from "@/pages/fbs/SuppliesPage";
import { FbsPickPage } from "@/pages/fbs/PickPage";
import { FbsTransferOrdersPage } from "@/pages/fbs/TransferOrdersPage";
import { ShipmentPage } from "@/pages/shipment/ShipmentPage";
import { IntakeListPage } from "@/pages/returns/IntakeListPage";
import { IntakeSessionPageV2 } from "@/pages/returns/v2/SessionPage";
import { NewSessionPage } from "@/pages/returns/NewSessionPage";
import { ProcessingDetailPage } from "@/pages/returns/ProcessingDetailPage";
import { ProcessingListPage } from "@/pages/returns/ProcessingListPage";
import { OrderCheckListPage } from "@/pages/order-check/OrderCheckListPage";
import { InventoryListPage } from "@/pages/inventory/InventoryListPage";
import { InventorySessionPage } from "@/pages/inventory/InventorySessionPage";
import { ReceivingListPage } from "@/pages/receiving/ReceivingListPage";
import { ReceivingDeliveryPage } from "@/pages/receiving/DeliveryPage";
import { ReceivingNewOrderPage } from "@/pages/receiving/NewOrderPage";
import { ReviewListPage } from "@/pages/receiving/ReviewListPage";
import { ReviewDeliveryPage } from "@/pages/receiving/ReviewDeliveryPage";
import { OrderJournalListPage } from "@/pages/order-journal/OrderJournalListPage";
import { OrderJournalPage } from "@/pages/order-journal/OrderJournalPage";
import { OkkListPage } from "@/pages/okk-receiving/OkkListPage";
import { OkkInspectionPage } from "@/pages/okk-receiving/OkkInspectionPage";
import { OrderCheckSessionPageV2 } from "@/pages/order-check/v2/SessionPage";
import { PalletPage } from "@/pages/order-check/v2/PalletPage";
import { OrderPlanListPage } from "@/pages/order-plan/OrderPlanListPage";
import { PlanReviewPage } from "@/pages/order-plan/PlanReviewPage";
import { TransferCheckListPage } from "@/pages/transfer-check/TransferCheckListPage";
import { TransferCheckSessionPageV2 } from "@/pages/transfer-check/v2/SessionPage";
import { TransferReceiptListPage } from "@/pages/transfer-receipts/ReceiptListPage";
import { TransferReceiptPage } from "@/pages/transfer-receipts/ReceiptPage";
import { TransferDiscrepancyListPage } from "@/pages/transfer-discrepancies/DiscrepancyListPage";
import { TransferDiscrepancyPage } from "@/pages/transfer-discrepancies/DiscrepancyPage";
import { LabelInfoPage } from "@/pages/label-info/LabelInfoPage";
import { StockTransferListPage } from "@/pages/stock-transfer/StockTransferListPage";
import { StockTransferSessionPage } from "@/pages/stock-transfer/StockTransferSessionPage";
import { LogisticsListPage } from "@/pages/logistics/LogisticsListPage";
import { BoxPage } from "@/pages/boxes/BoxPage";
import { BoxScanPage } from "@/pages/boxes/BoxScanPage";
import { BoxesListPage } from "@/pages/boxes/BoxesListPage";
import { CargoRoute } from "@/pages/boxes/CargoPage";
import { SavedAddressesPage } from "@/pages/logistics/SavedAddressesPage";
import { TripPage } from "@/pages/logistics/TripPage";
import { WarehouseDashboardPage } from "@/pages/warehouse-dashboard/WarehouseDashboardPage";
import { WarehouseOpsPage } from "@/pages/warehouse-ops/WarehouseOpsPage";
import { warehouseDashboardLegacyRoutes } from "@/pages/warehouse-dashboard/routes";
import { Connection1CPage } from "@/pages/settings/Connection1CPage";
import { ScanTestPage } from "@/pages/scan-test/ScanTestPage";
import {
  FbsOzonSettingsTab,
  FbsSettingsPage,
  FbsStockSettingsTab,
  FbsWbSettingsTab,
} from "@/pages/settings/FbsSettingsPage";
import { GeneralSettingsPage } from "@/pages/settings/GeneralSettingsPage";
import { InventorySettingsPage } from "@/pages/settings/InventorySettingsPage";
import { LogisticsSettingsPage } from "@/pages/settings/LogisticsSettingsPage";
import { OrderCheckSettingsPage } from "@/pages/settings/OrderCheckSettingsPage";
import { PrintingSettingsPage } from "@/pages/settings/PrintingSettingsPage";
import { ReceivingSettingsPage } from "@/pages/settings/ReceivingSettingsPage";
import { ReturnsSettingsPage } from "@/pages/settings/ReturnsSettingsPage";
import { SettingsHubPage } from "@/pages/settings/SettingsHubPage";
import { StockSyncWarehousePage } from "@/pages/settings/StockSyncWarehousePage";
import { StockTransferSettingsPage } from "@/pages/settings/StockTransferSettingsPage";
import { TransferCheckSettingsPage } from "@/pages/settings/TransferCheckSettingsPage";
import { TransferReceiptSettingsPage } from "@/pages/settings/TransferReceiptSettingsPage";
import { WarehouseDashboardSettingsPage } from "@/pages/settings/WarehouseDashboardSettingsPage";
import { WarehouseTariffsSettingsPage } from "@/pages/settings/WarehouseTariffsSettingsPage";

/** Страницы секций настроек: адрес, ключ секции (её видимость считает
 *  бэкенд) и сама страница. Таблицей, а не девятью копиями `ProtectedRoute`:
 *  забыть в копии `requireSection` — значит открыть админу страницу секции,
 *  которой в инсталляции нет. */
export interface SectionRoute {
  path: string;
  section: string;
  element: React.ReactElement;
  /** Право вместо «только админ»: страницу открывает и обладатель права
   *  (админ проходит всегда). Без поля — `requireAdmin`, как у всех секций. */
  permission?: string;
}

export const SECTION_ROUTES: SectionRoute[] = [
  { path: "settings/general", section: "general", element: <GeneralSettingsPage /> },
  { path: "settings/printing", section: "printing", element: <PrintingSettingsPage /> },
  {
    path: "settings/order-check",
    section: "order_check",
    element: <OrderCheckSettingsPage />,
  },
  {
    path: "settings/transfer-check",
    section: "transfer_check",
    element: <TransferCheckSettingsPage />,
  },
  {
    path: "settings/stock-transfer",
    section: "stock_transfer",
    element: <StockTransferSettingsPage />,
  },
  {
    // Склады с подтверждением и привязки принимающих — только админ.
    path: "settings/transfer-receipts",
    section: "transfer_receive",
    element: <TransferReceiptSettingsPage />,
  },
  { path: "settings/receiving", section: "receiving", element: <ReceivingSettingsPage /> },
  // Договоры комиссии обслуживают обработку возвратов — без секции
  // «Возвраты» страницы нет (её роутера на бэкенде тоже нет).
  { path: "settings/returns", section: "returns", element: <ReturnsSettingsPage /> },
  { path: "settings/inventory", section: "inventory", element: <InventorySettingsPage /> },
  { path: "settings/logistics", section: "logistics", element: <LogisticsSettingsPage /> },
  {
    path: "settings/warehouse-dashboard",
    section: "warehouse_dashboard",
    element: <WarehouseDashboardSettingsPage />,
  },
  {
    path: "settings/warehouse-billing",
    section: "warehouse_billing",
    element: <WarehouseTariffsSettingsPage />,
    // Смотреть тарифы — право `warehouse_billing` (правка — `warehouse_tariffs`,
    // её проверяет сама страница); админом быть не обязательно.
    permission: "warehouse_billing",
  },
];

/** Страница секции настроек под своей защитой. */
export function sectionRouteElement({ section, element, permission }: SectionRoute): React.ReactElement {
  return permission ? (
    <ProtectedRoute requirePermission={permission} requireSection={section}>
      {element}
    </ProtectedRoute>
  ) : (
    <ProtectedRoute requireAdmin requireSection={section}>
      {element}
    </ProtectedRoute>
  );
}

/** Старый адрес «Приёмки (новой)» → тот же экран под общим префиксом. */
function RedirectToReceiving({ what }: { what: "orders" | "deliveries" }): React.ReactElement {
  const params = useParams();
  const id = what === "orders" ? params.ref : params.deliveryId;
  return <Navigate to={`/receiving/${what}/${id ?? ""}`} replace />;
}

export const router = createBrowserRouter([
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      // Личное подключение 1С — не настройка инсталляции, а учётка человека:
      // живёт в меню аккаунта и под `/account`.
      { path: "account/1c", element: <Connection1CPage /> },
      // Диагностика сканера (кольцо HID, ТСД) — для любого вошедшего, в обеих
      // инсталляциях, без бэкенда: раздела и права у неё нет.
      { path: "scan-test", element: <ScanTestPage /> },
      {
        path: "settings",
        element: (
          <ProtectedRoute requireAdmin>
            <SettingsHubPage />
          </ProtectedRoute>
        ),
      },
      ...SECTION_ROUTES.map((route) => ({
        path: route.path,
        element: sectionRouteElement(route),
      })),
      {
        path: "fbs",
        element: (
          <ProtectedRoute requirePermission="fbs">
            <FbsHomePage />
          </ProtectedRoute>
        ),
      },
      {
        // Экран сбора привязан к ЮРЛИЦУ, а не к поставке: поставка заводится
        // первым сканом, а до него её ещё нет.
        path: "fbs/cabinets/:cabinetId/pick",
        element: (
          <ProtectedRoute requirePermission="fbs">
            <FbsPickPage />
          </ProtectedRoute>
        ),
      },
      {
        // Завоз в комнату: портал формирует заказы на перемещение по юрлицам,
        // выполняет их «Проверка перемещений». Экран не скан-овый — id не нужен.
        path: "fbs/transfer-orders",
        element: (
          <ProtectedRoute requirePermission="fbs">
            <FbsTransferOrdersPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "fbs/cabinets/:cabinetId/supplies",
        element: (
          <ProtectedRoute requirePermission="fbs">
            <FbsSuppliesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "fbs/supplies/:supplyId/finish",
        element: (
          <ProtectedRoute requirePermission="fbs">
            <FbsFinishPage />
          </ProtectedRoute>
        ),
      },
      {
        // Проверка отгрузки: смена одна на всех, id в адресе не нужен.
        path: "shipment",
        element: (
          <ProtectedRoute requirePermission="shipment_check">
            <ShipmentPage />
          </ProtectedRoute>
        ),
      },
      // Настройки ФБС — одна секция с вкладками по маркетплейсам: общая шапка
      // и вкладки живут в родительском маршруте, содержимое вкладки — в
      // дочернем. Сам `/settings/fbs` адреса не имеет, ведёт на первую вкладку.
      {
        path: "settings/fbs",
        element: (
          <ProtectedRoute requireAdmin requireSection="fbs">
            <FbsSettingsPage />
          </ProtectedRoute>
        ),
        children: [
          { index: true, element: <Navigate to="/settings/fbs/wb" replace /> },
          {
            path: "wb",
            element: (
              <ProtectedRoute requirePage="fbs">
                <FbsWbSettingsTab />
              </ProtectedRoute>
            ),
          },
          {
            path: "ozon",
            element: (
              <ProtectedRoute requirePage="ozon_fbs">
                <FbsOzonSettingsTab />
              </ProtectedRoute>
            ),
          },
          // Остатки — общая вкладка обеих площадок: комната и синхронизация
          // остатков не принадлежат ни WB, ни Ozon.
          { path: "stock", element: <FbsStockSettingsTab /> },
        ],
      },
      // Склад площадки в синке — отдельный экран, а не вкладка: таблице расчёта
      // нужна ширина, которой у секции настроек (`max-w-2xl`) нет.
      {
        path: "settings/fbs/stock/:marketplace/:warehouseKey",
        element: (
          <ProtectedRoute requireAdmin requireSection="fbs">
            <StockSyncWarehousePage />
          </ProtectedRoute>
        ),
      },
      {
        path: "ozon",
        element: (
          <ProtectedRoute requirePermission="ozon_fbs">
            <OzonHome />
          </ProtectedRoute>
        ),
      },
      {
        path: "ozon/cabinets/:cabinetId/pick",
        element: (
          <ProtectedRoute requirePermission="ozon_fbs">
            <OzonScanPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "ozon/supplies/:supplyId/finish",
        element: (
          <ProtectedRoute requirePermission="ozon_fbs">
            <OzonFinishPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "returns",
        element: (
          <ProtectedRoute requirePermission="returns_intake">
            <IntakeListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "returns/new",
        element: (
          <ProtectedRoute requirePermission="returns_intake">
            <NewSessionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "returns/sessions/:id",
        element: (
          <ProtectedRoute requirePermission="returns_intake">
            <IntakeSessionPageV2 />
          </ProtectedRoute>
        ),
      },
      {
        path: "returns-processing",
        element: (
          <ProtectedRoute requirePermission="returns_processing">
            <ProcessingListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "returns-processing/sessions/:id",
        element: (
          <ProtectedRoute requirePermission="returns_processing">
            <ProcessingDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "order-check",
        element: (
          <ProtectedRoute requirePermission="order_check">
            <OrderCheckListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "order-check/orders/:ref",
        element: (
          <ProtectedRoute requirePermission="order_check">
            <OrderCheckSessionPageV2 />
          </ProtectedRoute>
        ),
      },
      {
        // Раскладка — СВОЙ раздел со своим правом. Роут вне `/order-check/
        // orders/*` не только по смыслу: этот префикс иммерсивный, и страница
        // рисовалась бы без меню, а «назад» уводило на скан-экран сборщика.
        path: "order-plan",
        element: (
          <ProtectedRoute requirePermission="order_plan">
            <OrderPlanListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "order-plan/orders/:ref",
        element: (
          <ProtectedRoute requirePermission="order_plan">
            <PlanReviewPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "order-check/orders/:ref/pallets/:palletId",
        element: (
          <ProtectedRoute requirePermission="order_check">
            <PalletPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "label-info",
        element: (
          <ProtectedRoute requirePermission="label_info">
            <LabelInfoPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "stock-transfer",
        element: (
          <ProtectedRoute requirePermission="stock_transfer">
            <StockTransferListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "stock-transfer/sessions/:sessionId",
        element: (
          <ProtectedRoute requirePermission="stock_transfer">
            <StockTransferSessionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "inventory",
        element: (
          <ProtectedRoute requirePermission="inventory">
            <InventoryListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "warehouse-dashboard",
        element: (
          <ProtectedRoute requirePermission="warehouse_dashboard">
            <WarehouseDashboardPage />
          </ProtectedRoute>
        ),
      },
      // Оборачиваемость — вкладка дашборда; старый адрес ведёт на неё.
      ...warehouseDashboardLegacyRoutes,
      {
        // «Операции и стоимость» — под правом «Учёт операций склада»; правка
        // тарифов на странице — отдельным правом `warehouse_tariffs`.
        path: "warehouse-ops",
        element: (
          <ProtectedRoute requirePermission="warehouse_billing">
            <WarehouseOpsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "boxes",
        element: (
          <ProtectedRoute requirePermission="boxes">
            <BoxesListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "boxes/scan",
        element: (
          <ProtectedRoute requirePermission="boxes">
            <BoxScanPage />
          </ProtectedRoute>
        ),
      },
      {
        // Короб/паллета с упаковки (chz-station) — по коду, а не по id.
        path: "boxes/cargo/:code",
        element: (
          <ProtectedRoute requirePermission="boxes">
            <CargoRoute />
          </ProtectedRoute>
        ),
      },
      {
        path: "boxes/:boxId",
        element: (
          <ProtectedRoute requirePermission="boxes">
            <BoxPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "inventory/sessions/:sessionId",
        element: (
          <ProtectedRoute requirePermission="inventory">
            <InventorySessionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "receiving",
        element: (
          <ProtectedRoute requirePermission="receiving">
            <ReceivingListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "receiving/orders/:ref",
        element: (
          <ProtectedRoute requirePermission="receiving">
            <ReceivingNewOrderPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "receiving/deliveries/:deliveryId",
        element: (
          <ProtectedRoute requirePermission="receiving">
            <ReceivingDeliveryPage />
          </ProtectedRoute>
        ),
      },
      // Раздел «Приёмка (новая)» слит с основным: приход по накладной остался
      // один. Старые адреса ведут на новые, а не в 404 — вкладку склада не
      // закрывают сутками, и ссылка из закладки обязана открыться.
      { path: "receiving-new", element: <Navigate to="/receiving" replace /> },
      {
        path: "receiving-new/orders/:ref",
        element: <RedirectToReceiving what="orders" />,
      },
      {
        path: "receiving-new/deliveries/:deliveryId",
        element: <RedirectToReceiving what="deliveries" />,
      },
      {
        path: "receiving-review",
        element: (
          <ProtectedRoute requirePermission="receiving_review">
            <ReviewListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "receiving-review/deliveries/:deliveryId",
        element: (
          <ProtectedRoute requirePermission="receiving_review">
            <ReviewDeliveryPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "okk-receiving",
        element: (
          <ProtectedRoute requirePermission="okk_receiving">
            <OkkListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "okk-receiving/:inspectionId",
        element: (
          <ProtectedRoute requirePermission="okk_receiving">
            <OkkInspectionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "order-journal",
        element: (
          <ProtectedRoute requirePermission="order_journal">
            <OrderJournalListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "order-journal/:orderRef",
        element: (
          <ProtectedRoute requirePermission="order_journal">
            <OrderJournalPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "transfer-check",
        element: (
          <ProtectedRoute requirePermission="transfer_check">
            <TransferCheckListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "transfer-check/transfers/:ref",
        element: (
          <ProtectedRoute requirePermission="transfer_check">
            <TransferCheckSessionPageV2 />
          </ProtectedRoute>
        ),
      },
      {
        path: "transfer-receipts",
        element: (
          <ProtectedRoute requirePermission="transfer_receive">
            <TransferReceiptListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "transfer-receipts/:sessionId",
        element: (
          <ProtectedRoute requirePermission="transfer_receive">
            <TransferReceiptPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "transfer-discrepancies",
        element: (
          <ProtectedRoute requirePermission="transfer_discrepancy">
            <TransferDiscrepancyListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "transfer-discrepancies/:sessionId",
        element: (
          <ProtectedRoute requirePermission="transfer_discrepancy">
            <TransferDiscrepancyPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "logistics",
        element: (
          <ProtectedRoute requirePermission="logistics">
            <LogisticsListPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "logistics/addresses",
        element: (
          <ProtectedRoute requirePermission="logistics">
            <SavedAddressesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "logistics/trips/:id",
        element: (
          <ProtectedRoute requirePermission="logistics">
            <TripPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "admin/users",
        element: (
          <ProtectedRoute requireAdmin>
            <AdminUsersPage />
          </ProtectedRoute>
        ),
      },
      {
        // Справочник баз 1С для SSO-входа (пункт меню — только при
        // `sso_enabled`; ручки `/api/onec-bases` смонтированы всегда).
        path: "admin/onec-bases",
        element: (
          <ProtectedRoute requireAdmin>
            <OneCBasesAdminPage />
          </ProtectedRoute>
        ),
      },
      // Старые адреса настроек: вкладку склада не закрывают сутками, и ссылка
      // из закладки обязана открыться, а не упасть в 404.
      { path: "admin/system", element: <Navigate to="/settings/general" replace /> },
      { path: "settings/ozon", element: <Navigate to="/settings/fbs/ozon" replace /> },
      {
        path: "settings/marketplace-contracts",
        element: <Navigate to="/settings/returns" replace />,
      },
      { path: "settings/1c", element: <Navigate to="/account/1c" replace /> },
      {
        path: "admin/activity",
        element: (
          <ProtectedRoute requireAdmin>
            <ActivityPage />
          </ProtectedRoute>
        ),
      },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);
